import axios from 'axios';
import { BOOKING_ENTITY_URL, REFUND_API } from 'Constants';
import { buildUrl } from 'Util/FetchUtils';
import { serialiseDateTimeNoTz } from 'Util/AttributeUtils';
import { stringNotEmpty } from 'Util/TypeGuards';

export interface RefundInfoDto {
	refundMethods: CardsToRefundDto[];
	isInvoiced: boolean;
	isUnpaid: boolean;
	refundRequired: boolean;
}
export interface CardsToRefundDto {
	last4?: string;
	brand?: string;
	manualRefund: boolean;
	amountToRefund: number;
	creditRefund?: boolean;
}
export async function getRefundPopUpInfo(transactionId: string): Promise<RefundInfoDto> {
	const results = await axios.get(
		buildUrl(`${REFUND_API}/refund-pop-up-info`, {
			transactionId: transactionId ?? '',
		}),
	);

	return results.data as RefundInfoDto;
}

export type RefundListStatus =
	| 'COMPLETED'
	| 'PENDING'
	| 'FAILED'
	| 'CANCELLED'
	| 'PENDING_MANUAL'
	| 'MANUALLY_COMPLETED';

export interface RefundListSummaryDto {
	id: string;
	created: string;
	amount: number | null;
	status: RefundListStatus;
	providerRefundId: string | null;
	last4: string | null;
	cardBrand: string | null;
	note: string | null;
	paymentProvider: string;
	transactionId: string | null;
	transactionHumanReadableId: string | null;
	bookingId: string | null;
	/** The charter this refund came off, when it came off one. A charter refund has no booking. */
	charterId: string | null;
	charterName: string | null;
	userId: string | null;
	userName: string | null;
	userEmail: string | null;
	staffName: string | null;
	notes: string | null;
	isRefundedFromTransferredBooking: boolean;
	/** This refund is paying out a credit voucher, so it can't be converted back into a credit. */
	isFromCreditVoucher: boolean;
}

export interface RefundFilterOptions {
	searchString?: string;
	startDate?: Date;
	endDate?: Date;
	statusFilters?: RefundListStatus[];
	methodFilters?: string[];
	orderBy?: string;
	orderByDescending?: boolean;
	skip?: number;
	take?: number;
}

export async function fetchFilteredRefunds(
	options: RefundFilterOptions,
): Promise<{ data: RefundListSummaryDto[]; count: number }> {
	const params = new URLSearchParams();

	if (stringNotEmpty(options.searchString)) {
		params.append('searchString', options.searchString!);
	}

	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}

	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}

	(options.statusFilters ?? []).forEach(s => params.append('statusFilters', s));
	(options.methodFilters ?? []).forEach(m => params.append('methodFilters', m));

	if (options.orderBy !== undefined) {
		params.append('orderBy', options.orderBy);
	}
	if (options.orderByDescending !== undefined) {
		params.append('orderByDescending', String(options.orderByDescending));
	}
	if (options.skip !== undefined) {
		params.append('skip', options.skip.toString());
	}
	if (options.take !== undefined) {
		params.append('take', options.take.toString());
	}

	const response = await axios.get(`${REFUND_API}/fetch-filtered-refunds`, { params });

	return {
		data: response.data.data as RefundListSummaryDto[],
		count: response.data.count,
	};
}
